import { netService } from './NetService';
import { dungeonService } from './DungeonService';
import { useSpectatorStore } from '../store/spectatorSlice';
import {
  makeRunWipe,
  makePlayerDied,
  makePlayerRevived,
  makeWorldUpdate,
  type RunWipeReason,
} from '../net/sessionEvents';
import { reconnectRegistry } from '../net/reconnect';
import type { PlacedLocation } from '../types/map.types';
import type { PlayerEntity } from '../entities/PlayerEntity';

const REVIVE_HP_PERCENT = 0.5;
const BEACON_SPRITE = 'circle';
const BEACON_PLACEMENT = 'floor' as const;

/**
 * Host authority for the co-op death/revive state machine: the downed set,
 * beacons, TPK check, and revive execution. Runs only on the host. Local-player
 * resolution (seed decision, spectator, walk-to-revive) lives in CoopDeathClient.
 */
class CoopDeathHost {
  private downed = new Set<string>();
  private pendingDeath = new Set<string>();

  isDownedForReconnect(playerId: string): boolean {
    return this.downed.has(playerId);
  }

  /** Read-only snapshot of currently-downed player ids (for the co-op save). */
  downedPlayerIds(): string[] {
    return [...this.downed];
  }

  /** Restore the downed set on host resume (beacons come back via the saved
   *  placedLocations; this re-arms revive/TPK tracking). */
  restoreDowned(playerIds: string[]): void {
    for (const id of playerIds) {
      this.downed.add(id);
    }
  }

  private beaconId(playerId: string): string {
    return `revive:${playerId}`;
  }

  handleHostPlayerDeath(player: PlayerEntity): void {
    if (!netService.isHost() || this.downed.has(player.id)) {
      return;
    }
    const cell = { x: player.positionX, y: player.positionY };
    // Remove the entity NOW, not via the death animation's deferred removeEntity
    // (~500ms later): a revive may re-create an entity with the same id in the
    // meantime, and the stale deferred removal would nuke it. Removing now also
    // stops the dying entity from being ticked, so that callback never fires.
    dungeonService.removeEntity(player.id);
    if (player.id === dungeonService.localPlayerId) {
      // Host's own player: the `onDeath` seed short-circuit already ran against
      // the host inventory, so reaching here means no seed → go down directly.
      this.downPlayer(player.id, cell);
      return;
    }
    // Owner offline: the request/response death model assumes the client is
    // present to decide its seed. A disconnected avatar can't answer → down it
    // directly (no seed; inventory is client-local and unreachable).
    if (player.disconnected) {
      this.downPlayer(player.id, cell);
      return;
    }
    // A client owns its (client-local) inventory, so the host can't know whether
    // it has a seed. Ask it; it answers with `death-resolution` (revive vs down).
    this.pendingDeath.add(player.id);
    netService.session?.broadcast('reliable', makePlayerDied(player.id, cell));
  }

  /** Actually put a player down — beacon, spectator (if local), TPK check. */
  private downPlayer(playerId: string, cell: { x: number; y: number }): void {
    if (this.downed.has(playerId)) {
      return;
    }
    this.downed.add(playerId);
    const beacon = this.placeBeacon(playerId, cell.x, cell.y);
    if (beacon) {
      netService.session?.broadcast(
        'reliable',
        makeWorldUpdate([{ op: 'add', location: beacon }]),
      );
    }
    if (playerId === dungeonService.localPlayerId) {
      useSpectatorStore.getState().setDowned(true);
    }
    this.checkTpk();
  }

  /** Apply a client's own death outcome. */
  handleDeathResolution(
    playerId: string,
    cell: { x: number; y: number },
    revived: boolean,
  ): void {
    if (!netService.isHost()) {
      return;
    }
    // Only honour a resolution for a player we actually reported dead — a forged
    // death-resolution for a live (self-)player would otherwise grant a free
    // respawn/heal and could block the TPK.
    if (!this.pendingDeath.has(playerId)) {
      return;
    }
    this.pendingDeath.delete(playerId);
    if (revived) {
      this.respawnDeadPlayer(playerId, cell.x, cell.y);
    } else {
      this.downPlayer(playerId, cell);
    }
  }

  private placeBeacon(
    playerId: string,
    x: number,
    y: number,
  ): PlacedLocation | null {
    // Place on the given cell directly — the death cell is walkable ground and
    // the dying entity only occupies it transiently (removed after its death
    // animation), so it must not count as "occupied" or the beacon lands off the
    // body. Relocate only if ANOTHER location already sits there (two deaths on
    // the same cell).
    const cell = dungeonService.locationService.isLocationAt(x, y)
      ? dungeonService.monsterSpawningService.findFreeCellNear(x, y)
      : { x, y };
    if (!cell) {
      return null;
    }
    const beacon: PlacedLocation = {
      id: this.beaconId(playerId),
      x: cell.x,
      y: cell.y,
      width: 1,
      height: 1,
      placement: BEACON_PLACEMENT,
      roomIndex: -1,
      sprite: BEACON_SPRITE,
      // 'walk', not 'interact': 'interact' locations block movement
      // (isLocationBlockingAt), which would make the beacon cell unreachable and
      // the revive impossible. The `revive:` prefix guard in checkTrapAtPosition
      // keeps the normal walk-trap handler from firing; revive runs via
      // checkReviveAt instead.
      interactionType: 'walk',
    };
    dungeonService.locationService.addPlacedLocation(beacon);
    return beacon;
  }

  /** A client asks to revive a downed ally. The requester must be a live run
   *  player; the seed itself is consumed client-side (inventory is local). */
  handleReviveRequest(fromPeerId: string, deadPlayerId: string): void {
    if (!netService.isHost()) {
      return;
    }
    // A reconnected reviver sends from a NEW peerId; resolve it to its avatar id
    // so the run-membership / not-downed checks match.
    const requesterId = reconnectRegistry.resolvePlayerId(fromPeerId);
    if (
      !dungeonService.runPlayerIds.has(requesterId) ||
      this.downed.has(requesterId)
    ) {
      return;
    }
    this.reviveOnHost(deadPlayerId);
  }

  reviveOnHost(deadPlayerId: string): void {
    if (!netService.isHost() || !this.downed.has(deadPlayerId)) {
      return;
    }
    const beacon = dungeonService.locationService
      .getPlacedLocations()
      .find((l) => l.id === this.beaconId(deadPlayerId));
    if (!beacon) {
      return;
    }
    const cell = dungeonService.monsterSpawningService.findFreeCellNear(
      beacon.x,
      beacon.y,
    );
    if (!cell) {
      return;
    }
    this.downed.delete(deadPlayerId);
    dungeonService.locationService.removePlacedLocation(
      this.beaconId(deadPlayerId),
    );
    this.respawnDeadPlayer(deadPlayerId, cell.x, cell.y);
    netService.session?.broadcast(
      'reliable',
      makePlayerRevived(deadPlayerId, cell),
    );
    netService.session?.broadcast(
      'reliable',
      makeWorldUpdate([{ op: 'remove', id: this.beaconId(deadPlayerId) }]),
    );
    if (deadPlayerId === dungeonService.localPlayerId) {
      useSpectatorStore.getState().setDowned(false);
    }
  }

  private respawnDeadPlayer(playerId: string, x: number, y: number): void {
    const isLocal = playerId === dungeonService.localPlayerId;
    const snap = isLocal
      ? undefined
      : dungeonService.remoteLoadouts.get(playerId);
    const player = dungeonService.createPlayerEntity(x, y, snap);
    player.id = playerId;
    if (!isLocal) {
      player.remote = true;
      player.masteryLevels = snap?.mastery ?? {};
    }
    dungeonService.buildPlayerRunEffects(player);
    dungeonService.addEntity(player);
    player.life = Math.max(1, Math.round(player.maxLife * REVIVE_HP_PERCENT));
  }

  respawnBeaconsForFloor(): void {
    if (!netService.isHost()) {
      return;
    }
    for (const playerId of this.downed) {
      const pos =
        dungeonService.monsterSpawningService.findValidSpawnPosition();
      if (pos) {
        this.placeBeacon(playerId, pos.x, pos.y);
      }
    }
  }

  /** Wipe the whole run for everyone (TPK or deliberate host abandon): clients
   *  get `run-wipe` (the `reason` drives their notice), the host runs the local
   *  coop-wipe path (recovery + shared hub + clearRunSave). The session stays
   *  open — everyone reconvenes in the hub. */
  wipeRun(reason: RunWipeReason): void {
    if (!netService.isHost()) {
      return;
    }
    netService.session?.broadcast('reliable', makeRunWipe(reason));
    dungeonService.emitGameEvent('coop-wipe');
  }

  /** Host: a player deliberately left the run. Clean its death-state (downed
   *  entry + beacon) if any, then re-check TPK. The caller has already removed
   *  the entity, its registry token, and its runPlayerIds membership. */
  handlePlayerLeftRun(playerId: string): void {
    if (!netService.isHost()) {
      return;
    }
    this.pendingDeath.delete(playerId);
    if (this.downed.has(playerId)) {
      this.downed.delete(playerId);
      const id = this.beaconId(playerId);
      dungeonService.locationService.removePlacedLocation(id);
      netService.session?.broadcast(
        'reliable',
        makeWorldUpdate([{ op: 'remove', id }]),
      );
    }
    this.checkTpk();
  }

  private checkTpk(): void {
    if (!netService.isHost()) {
      return;
    }
    const alive = [...dungeonService.runPlayerIds].filter(
      (id) => !this.downed.has(id),
    );
    if (alive.length === 0) {
      this.wipeRun('tpk');
    }
  }

  reset(): void {
    this.downed.clear();
    this.pendingDeath.clear();
  }
}

export const coopDeathHost = new CoopDeathHost();
